/**
 * OPA / SCT — Operation Analysis and Standard Cycle Time.
 *
 * A tab on the costing screen (Max, 2026-08-29). One document per style per season: every sewing
 * operation with its needle, machine, sewing length, part count and cycle time, rolled up into the
 * SCT in hours that costing needs.
 *
 * Built from the team's own two files — `docs/S27 FORMAT.xlsx` (the blank) and
 * `docs/CHAIR ONE_S27_OPA_20260811.xlsx` (a filled one). The arithmetic is in src/opaModel.js,
 * transcribed from the workbook's formulas and checked against CHAIR ONE, whose SCT is 1.52.
 *
 * IMPORT EXISTS BECAUSE RETYPING DOES NOT SCALE. CHAIR ONE alone is 46 operations and a real season is
 * dozens of styles. An .xlsx in the team's layout is read here, shown, and only written when somebody
 * presses Save — an importer that goes straight to the database is one bad file from a mess.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TabIcon from './TabIcon';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList, ResponsiveContainer,
} from 'recharts';
import { Plus, Trash2, Save, Upload, FileDown, FileSpreadsheet, FileText, Copy, X, ArrowLeft, Eye, Pencil, List, FilterX, Library, BookmarkPlus, ImagePlus, ChevronRight, ChevronUp, ChevronDown } from 'lucide-react';
import { SortTh, sortRows, rowPassesColumnFilters, distinctColumnValues } from './15_SampleTracker';
import useFrozenColumns from './useFrozenColumns';
import SctSummary from './32_SctSummary';
import SctActual from './SctActual';
import { BACKEND_URL } from './backendConfig';
import ProjectPhotos, { compressImage } from './ProjectPhotos';
import './ProjectPhotos.css';
import { OPA_SECTIONS, OPA_SECTION_LABEL, opaSeconds, opaTotals, opaLineSeconds, opaSectionMult, opaRound } from './opaModel';

/* No `factory`. The S27 format has no Factory row — that came off NEW CONSTRUCTION CORNER, the other
   sheet in the example workbook, and Max ruled it out of scope on 2026-08-29: this is built from
   CHAIR ONE S27 and nothing else. */
/* Max, 2026-09-03. Held here rather than in a dimension table or a CHECK constraint, the same call
   the season made: the set is Yen's to change and a fourth value should be one line, not a migration. */
export const OPA_CATEGORIES = ['Inline', 'Special Project', 'FreeFlow'];

/* The list's columns. `num` right-aligns and sorts numerically; the Action column is not here
   because there is nothing to sort or filter it by. */
const LIST_COLUMNS = [
  { key: 'docNo', label: 'Doc' },
  { key: 'styleName', label: 'Style', cls: 'opa-style' },
  { key: 'season', label: 'Season' },
  /* Line and Category as the SCT summary means them — the product's, read off its SKU's hierarchy,
     not the document's own Inline / Special Project / FreeFlow (Yen via Max, 2026-09-05: "đưa cột
     line & category qua phần OPA documents cho dễ tìm theo nhóm"). They sit next to Season so a
     reader groups by them before reading a number, and both sort and filter like every other column. */
  { key: 'line', label: 'Line' },
  { key: 'plCategory', label: 'Category' },
  { key: 'category', label: 'Doc type' },
  { key: 'skuCount', label: 'SKUs', num: true },
  /* SCT ahead of its own parts (Max, 2026-09-03). It is the number the document exists to produce;
     Skin / Case / ACC are the working that gets you there and read as a breakdown after it. */
  { key: 'sct', label: 'SCT (h)', num: true },
  { key: 'skin', label: 'Skin', num: true },
  { key: 'caseH', label: 'Case', num: true },
  { key: 'acc', label: 'ACC', num: true },
  { key: 'version', label: 'Ver', num: true },
  { key: 'created', label: 'Created' },
  { key: 'createdBy', label: 'Created by' },
  { key: 'updated', label: 'Updated' },
  { key: 'updatedBy', label: 'Updated by' },
];
/* The three identity columns freeze to the left and Action freezes to the right (Max, 2026-08-30).
   Sticky needs a REAL offset, so the widths are declared here rather than measured: measuring live
   widths would reflow on every render, and the header and the body would disagree mid-scroll. */
/* Four frozen columns: No, Doc, Style, Season. The widths below are what they ASK for; the offsets
   that pin them come from useFrozenColumns, which measures what they actually got. */
const FROZEN_KEYS = ['docNo', 'styleName', 'season'];
const FROZEN = { docNo: 108, styleName: 200, season: 92 };   /* 92: the caret needs room beside the word */
/* Widths for the four who/when columns (Max, 2026-09-03). Left automatic they size to their HEADER —
   "Created by" is wider than every name under it — and four of them pushed the table far enough past
   the container that the right-hand Action block sat on top of the last one at rest. These are not
   frozen; they are just a cap so the table stops growing for the sake of the labels. */
const LIST_WIDTH = {
  line: 104, plCategory: 116, category: 104,
  created: 92, createdBy: 88, updated: 92, updatedBy: 88,
};

const LIST_GETTERS = Object.fromEntries(LIST_COLUMNS.map((c) => [
  c.key,
  c.num ? (row) => Number(row[c.key] ?? 0) : (row) => row[c.key] || '',
]));

const BLANK_HEAD = {
  styleName: '', variant: '', season: '', category: '', skus: '',
  styleEfficiency: 1, indirectRate: 0.25,
  frameFitMin: 0, frameAssMin: 0, packagingMin: 0,
  docDate: new Date().toISOString().slice(0, 10), version: 1, notes: '',
  /* The four buckets are fixed, but what a product calls them is not: the S27 storage boxes head
     their block "I. BOX", the stools "III. TIE CORD", the games "III. BEAN BAG x 1". An imported
     document keeps its sheet's wording here; empty means the chair anatomy. */
  sectionTitles: {},
  /* Pieces per product where a block describes one of several — see opaSectionMult. */
  sectionMult: {},
};
const BLANK_LINE = (section) => ({
  section, stageEn: '', stageVn: '', needle: '', machine: '',
  sewingLengthCm: '', partQty: '', ctPerPartSec: '', isDifficult: false,
  /* One photo per operation (Max, 2026-09-03). A POINTER into the shared photo store, not the image:
     saving DELETEs every operation row and re-INSERTs it, so the id has to travel with the line. */
  photoId: null,
});
const PHOTO_URL = (id) => `${BACKEND_URL}/api/development/photos/file/${id}`;
/* The team's own chart colours: skin blue, case amber. The other two sections have no chart on the
   sheet, so they get the portal accent rather than an invented pair. */
const SECTION_COLOUR = {
  SKIN: '#3b6fd4', CASE: '#e8b53d', ACC1: '#17A2B8', ACC2: '#8B5CF6',
  ACC3: '#e2725b', ACC4: '#3f9e6b', ACC5: '#8a8f98',
};
const n2 = (v) => Number(v || 0).toFixed(2);
/* The list column for SKUs went from raw text to a summary to, finally, an icon with the count
   (Max, 2026-08-30) — the codes themselves say nothing at a glance, and the icon opens a viewer
   that resolves them to SKU / code / style / colour. */
const skuCell = (skus) => {
  const raw = String(skus || '');
  const tokens = raw.split(raw.includes(';') ? ';' : ',').map((v) => v.trim()).filter(Boolean);
  return { count: tokens.length };
};
const int = (v) => Number(v || 0).toLocaleString('en-GB');

/* 0.74 -> "74". Through 10000 and back, because 0.74 * 100 is 74.00000000000001 in binary and the
   box would show that. */
const pctText = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const n = Number(value);
  return Number.isFinite(n) ? String(Math.round(n * 10000) / 100) : '';
};

/* A translated sentence with elements inside it (keys, a bold word): the placeholder is filled with a
   marker, the string split on it and the element dropped in, so each language keeps its own word order. */
// The markers are control characters on purpose: they cannot occur in any translation.
// eslint-disable-next-line no-control-regex
const fillParts = (text, parts) => text.split(/(\u0001\w+\u0002)/).map((piece, i) => {
  // eslint-disable-next-line no-control-regex
  const m = piece.match(/^\u0001(\w+)\u0002$/);
  // eslint-disable-next-line react/no-array-index-key
  return m ? <React.Fragment key={i}>{parts[m[1]]}</React.Fragment> : piece;
});
const marker = (name) => `\u0001${name}\u0002`;

export default function OpaSct({ apiBase }) {
  const { t } = useTranslation();
  /* The list's headings on screen. LIST_COLUMNS keeps the English, which is also what SCT (h), Skin,
     Case and ACC show — those are the workbook's own words. */
  const COL_LABEL = {
    docNo: t('xOpa.col.docNo'), styleName: t('xOpa.col.styleName'), season: t('xOpa.col.season'),
    line: t('xOpa.col.line'), plCategory: t('xOpa.col.plCategory'), category: t('xOpa.col.category'),
    skuCount: t('xOpa.col.skuCount'), version: t('xOpa.col.version'), created: t('xOpa.col.created'),
    createdBy: t('xOpa.col.createdBy'), updated: t('xOpa.col.updated'), updatedBy: t('xOpa.col.updatedBy'),
  };
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [view, setView] = useState('list');          // 'list' | 'edit'
  /* Read-only is a MODE of the same screen, not a second component. The alternative — a view
     component beside the editor — means every column, every total and every chart exists twice and
     the two drift; this way the only difference is whether a cell is an input. */
  const [readOnly, setReadOnly] = useState(false);
  /* What was loaded, so "has anything changed" is a comparison rather than a flag somebody has to
     remember to set on every edit path. */
  const [snapshot, setSnapshot] = useState('');
  const [leaving, setLeaving] = useState(null);      // the action waiting on keep-editing / discard
  /* The SKUs picker. One OPA applies to several SKU styles (Max, 2026-08-30), so SKUs is a set of
     style names picked from the style master, not a text box. The set itself keeps living in
     head.skus as a ', '-joined string — the storage, the export and the dirty-tracking all already
     work on that, and a second representation of the same fact would drift from the first. */
  const [skuOpen, setSkuOpen] = useState(false);
  const [skuSearch, setSkuSearch] = useState('');
  const [skuNames, setSkuNames] = useState([]);
  const [skuLoading, setSkuLoading] = useState(false);
  const [skuShowPicked, setSkuShowPicked] = useState(false);
  /* 149 documents arrived with the S27 folder on 2026-08-30 — the list needs a way in. The search
     box is the quick one; the column menus are the Excel one Max asked for the next day, and they
     are the Sample Tracker's, not a second implementation of the same panel. */
  /* Standard sewing blocks (Yến, 2026-09-02). Two thirds of the operation rows in the 149 imported
     documents are an exact repeat of a row in another one — "Attach eyelet x 1" appears in 38. A
     block is a named group saved once and COPIED into the next document that needs it. */
  const [blocks, setBlocks] = useState([]);
  const [picked, setPicked] = useState(() => new Set());   // rows ticked in the current section
  const [saveBlock, setSaveBlock] = useState(null);        // { name, note }
  const [insertOpen, setInsertOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(null);   // block id whose operations are shown
  /* Which operations of the open block will be inserted. Everything, until somebody says otherwise —
     taking the whole block is the common case and an empty selection would make the button a trap. */
  const [blockPick, setBlockPick] = useState(null);   // Set of indices, or null while nothing is open
  const [blockErr, setBlockErr] = useState('');
  /* Search the saved blocks — Yen via Max, 2026-09-05: "có thể lọc dữ liệu trong file lưu trữ đc
     ko?". It reads the OPERATIONS as well as the name, because what somebody is looking for is
     usually a step ("pole pocket"), not a block title they would have to remember. The rows are
     already in hand — opaBlockLines sends them with the list — so this costs no request. */
  const [blockSearch, setBlockSearch] = useState('');
  const [dropBlock, setDropBlock] = useState(null);   // the block awaiting confirmation

  const [search, setSearch] = useState('');
  /* The SCT summary started as its own page and Max moved it in here on 2026-08-30: it is the same
     data read the other way round — a document per style, or a row per SKU — and two menu entries
     for that is one too many. */
  /* ?tab=summary | actual opens that tab — the bell's cycle-time reminder links to ?tab=actual (2026-10-25). */
  const [tab, setTab] = useState(() => {
    const q = new URLSearchParams(window.location.search).get('tab');
    return ['summary', 'actual'].includes(q) ? q : 'docs';
  });
  const [listSort, setListSort] = useState({ key: '', dir: 'asc' });
  const [listFilters, setListFilters] = useState({});
  const [listOpenFilter, setListOpenFilter] = useState(null);

  /* docs.length matters: the first measurement runs on a table with no rows, whose columns are
     sized by the headings alone, and every offset would stay at that width. */
  const [listRef, frozenLeft] = useFrozenColumns(4, [tab, docs.length, search, listSort, listFilters]);
  /* The read-only look at a LIST row's SKUs (Max, 2026-08-30): the column used to print the codes
     and even summarised it was noise — an icon opens this instead, resolved to SKU / code / colour. */
  const [skuView, setSkuView] = useState(null);   // { doc, rows, loading }
  const [docId, setDocId] = useState(null);
  const [docNo, setDocNo] = useState('');
  const [head, setHead] = useState(BLANK_HEAD);
  const [lines, setLines] = useState([]);
  const [section, setSection] = useState('SKIN');
  const [confirmDel, setConfirmDel] = useState(null);
  const [dupId, setDupId] = useState(null);          // the row whose copy is in flight
  const [photoBusy, setPhotoBusy] = useState(null);  // the operation whose upload is in flight
  const [lightbox, setLightbox] = useState(null);    // photoId being viewed full size
  const photoInput = useRef(null);
  const photoFor = useRef(null);                     // which operation the file picker was opened for
  const [imported, setImported] = useState(null);    // sheets parsed out of an uploaded file
  const [pickedSheets, setPickedSheets] = useState(() => new Set());  // ticked for a bulk save
  const [bulkSave, setBulkSave] = useState(null);    // { done, total } while saving, then null
  const [bulkResult, setBulkResult] = useState(null);// { saved, failed: [{sheet, error}] }

  const loadList = useCallback(async () => {
    try {
      const response = await fetch(`${apiBase}/opa`, { credentials: 'include' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || i18n.t('xOpa.errLoadList'));
      setDocs(data.docs || []);
    } catch (err) { setError(err.message); }
  }, [apiBase]);
  useEffect(() => { loadList(); }, [loadList]);

  /* /costing/opa-sct?doc=5 opens that document. The SCT summary has linked this way since it was
     built, and nothing read the parameter — the link reloaded the app and landed on the list, which
     looked like the link was dead (Max, 2026-09-03). Kept as a URL as well as an in-page callback so
     the link stays pasteable and "open in new tab" still works.
     Runs once: `docId` is not in the deps, or closing the document would immediately reopen it. */
  const openedFromUrl = useRef(false);
  useEffect(() => {
    if (openedFromUrl.current) return;
    const wanted = Number(new URLSearchParams(window.location.search).get('doc'));
    if (!Number.isInteger(wanted) || wanted <= 0) return;
    openedFromUrl.current = true;
    openDoc(wanted, 'view');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /* ?docNo=OPA-000001 — the number people read and the assistant (Catchy) prints, which is NOT the row
     id `doc` carries (they agree on 1 of 158 documents). So it waits for the list and opens the
     document whose number matches; once, like the one above. */
  useEffect(() => {
    if (openedFromUrl.current || !docs.length) return;
    const no = (new URLSearchParams(window.location.search).get('docNo') || '').trim().toUpperCase();
    if (!no) return;
    const hit = docs.find((d) => String(d.docNo || '').toUpperCase() === no);
    if (!hit) return;
    openedFromUrl.current = true;
    openDoc(hit.id, 'view');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs]);

  /* The live figures. Recomputed from the rows on every keystroke rather than fetched, because the
     whole value of the screen is watching SCT move while an operation is being timed. */
  /* This document's word for a bucket, falling back to the chair anatomy. */
  const secLabel = (sec) => (head.sectionTitles && head.sectionTitles[sec]) || OPA_SECTION_LABEL[sec];
  const secShort = (sec) => {
    const bare = secLabel(sec).replace(/^[IVX]+\.\s*/, '').trim();
    const name = bare.charAt(0).toUpperCase() + bare.slice(1).toLowerCase();
    const m = opaSectionMult(head, sec);
    return m > 1 ? `${name} x ${m}` : name;
  };

  /* One flat row per document, so the sort, the column filter and the cell all read the SAME value —
     a filter offering something the column never displays is how those three drift apart. */
  const accHours = (d) => opaRound(['ACC1', 'ACC2', 'ACC3', 'ACC4', 'ACC5']
    .reduce((sum, k) => sum + (d.totals.hours[k] || 0), 0));

  const listRows = useMemo(() => docs.map((d) => ({
    doc: d,
    docNo: d.docNo,
    styleName: `${d.styleName || ''}${d.variant ? ` ${d.variant}` : ''}`,
    season: d.season || '',
    category: d.category || '',
    skuCount: skuCell(d.skus).count,
    skin: d.totals.hours.SKIN,
    caseH: d.totals.hours.CASE,
    /* One ACC column, not five. The document shows the blocks apart; the list is a summary, and
       ACC3-5 exist for the styles Yen has coming rather than the ones on screen today. */
    acc: accHours(d),
    sct: d.totals.sct,
    version: d.version,
    /* Who and when, on both ends (Max, 2026-09-03). Dates are sliced to the day here rather than
       formatted at render: the column menus group on this value, and a timestamp would give every
       row its own bucket. */
    created: d.createdAt ? String(d.createdAt).slice(0, 10) : '',
    createdBy: d.createdBy || '',
    updated: d.updatedAt ? String(d.updatedAt).slice(0, 10) : '',
    updatedBy: d.updatedBy || '',
  })), [docs]);

  /* Searched first, so the column menus offer the values you can actually see. */
  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return listRows;
    return listRows.filter((r) => `${r.docNo} ${r.styleName} ${r.season} ${r.doc.skus || ''}`
      .toLowerCase().includes(q));
  }, [listRows, search]);

  const shown = useMemo(() => {
    const kept = Object.keys(listFilters).length
      ? searched.filter((r) => rowPassesColumnFilters(r, listFilters, LIST_GETTERS))
      : searched;
    return listSort.key ? sortRows(kept, listSort, LIST_GETTERS) : kept;
  }, [searched, listFilters, listSort]);

  const listNarrowed = !!search.trim() || !!listSort.key || !!Object.keys(listFilters).length;
  const clearList = () => {
    setSearch(''); setListSort({ key: '', dir: 'asc' }); setListFilters({}); setListOpenFilter(null);
  };

  const listTh = (column) => ({
    label: COL_LABEL[column.key] || column.label,
    sortKey: column.key,
    sort: listSort,
    className: `${column.num ? 'num' : (column.cls || '')}`
      + (FROZEN[column.key] ? ` sticky-col${column.key === 'season' ? ' last-sticky' : ''}` : ''),
    style: FROZEN[column.key]
      ? { left: frozenLeft[FROZEN_KEYS.indexOf(column.key) + 1], width: FROZEN[column.key] }
      : undefined,
    onSort: (key, dir) => setListSort((prev) => ({
      key, dir: dir || (prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc'),
    })),
    filterValues: listOpenFilter?.key === column.key
      ? distinctColumnValues(searched, column.key, listFilters, LIST_GETTERS) : undefined,
    filterSelected: listFilters[column.key],
    filterOpen: listOpenFilter?.key === column.key,
    filterAnchor: listOpenFilter?.key === column.key ? listOpenFilter.anchor : null,
    onOpenFilter: (key, anchor) => setListOpenFilter({ key, anchor }),
    onCloseFilter: () => setListOpenFilter(null),
    onApplyFilter: (key, picked, all) => {
      setListFilters((prev) => {
        const next = { ...prev };
        if (!picked.length || picked.length === all.length) delete next[key];
        else next[key] = picked;
        return next;
      });
      setListOpenFilter(null);
    },
  });

  const seconds = useMemo(() => opaSeconds(lines), [lines]);
  const totals = useMemo(() => opaTotals(head, seconds), [head, seconds]);

  const setField = (key) => (event) => setHead((p) => ({ ...p, [key]: event.target.value }));

  /* Style efficiency is stored as the factor (0.74) and typed as a percentage (74). The box keeps
     its own text while it is being edited, so "74." on the way to "74.5" is not snapped back to
     "74" between keystrokes; on blur it goes back to reading the stored factor. */
  const [effTyping, setEffTyping] = useState(null);
  const effShown = effTyping !== null ? effTyping : pctText(head.styleEfficiency);
  const onEffChange = (event) => {
    const typed = event.target.value;
    setEffTyping(typed);
    setHead((p) => ({ ...p, styleEfficiency: typed === '' ? '' : Number(typed) / 100 }));
  };

  const nowState = useMemo(() => JSON.stringify({ head, lines }), [head, lines]);
  const dirty = view === 'edit' && !readOnly && snapshot !== '' && nowState !== snapshot;

  /* A refresh or a closed tab can only be caught by beforeunload, and the browser shows its own
     wording — a page cannot replace that dialog. Everything the PORTAL controls (Back, opening
     another document) goes through `leaving`, which is the portal's own popup. */
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; return ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  /* '; ' as the separator, not ', ': eight active style names contain a comma ("25FW LV x Helinox
     Chair (Skin, case)") and splitting those back would cut them in half. Reading falls back to ','
     for documents saved before the change. */
  const skuRaw = String(head.skus || '');
  const skuList = skuRaw.split(skuRaw.includes(';') ? ';' : ',').map((v) => v.trim()).filter(Boolean);
  const setSkuList = (next) => setHead((prev) => ({ ...prev, skus: next.join('; ') }));

  /* Server-side search: the style master holds 4,046 names, so the list is fetched per keystroke
     (debounced a beat) rather than shipped whole. */
  useEffect(() => {
    if (!skuOpen) return undefined;
    setSkuLoading(true);
    const timer = setTimeout(async () => {
      try {
        /* The picked view asks for the picked codes by name instead of searching — a pick made from
           a previous search would otherwise be invisible until somebody typed the right thing again. */
        const query = skuShowPicked
          ? `codes=${encodeURIComponent(skuList.join(';'))}`
          : `q=${encodeURIComponent(skuSearch)}`;
        const response = await fetch(
          `${apiBase}/opa/styles?${query}&exclude=${docId || 0}`, { credentials: 'include' });
        const data = await response.json();
        let rows = response.ok ? (data.names || []) : [];
        if (skuShowPicked) {
          /* A token saved before the picker existed ("Chair One") is not a material code, so the
             lookup cannot return it — but it IS picked, and hiding it here would make it
             un-untickable. It gets a plain row instead. */
          const found = new Set(rows.map((r) => r.sap));
          rows = rows.concat(skuList.filter((tok) => !found.has(tok))
            .map((tok) => ({ sap: tok, sku: '', name: t('xOpa.savedAsText'), color: '', usedBy: null })));
        }
        setSkuNames(rows);
      } catch (error) { setSkuNames([]); } finally { setSkuLoading(false); }
    }, 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skuOpen, skuSearch, skuShowPicked, apiBase, docId]);

  const openSkuView = async (d) => {
    const raw = String(d.skus || '');
    const tokens = raw.split(raw.includes(';') ? ';' : ',').map((v) => v.trim()).filter(Boolean);
    setSkuView({ doc: d, rows: [], loading: true });
    try {
      const response = await fetch(
        `${apiBase}/opa/styles?codes=${encodeURIComponent(tokens.join(';'))}&exclude=${d.id}`,
        { credentials: 'include' });
      const data = await response.json();
      let rows = response.ok ? (data.names || []) : [];
      const found = new Set(rows.map((r) => r.sap));
      rows = rows.concat(tokens.filter((tok) => !found.has(tok))
        .map((tok) => ({ sap: tok, sku: '', name: t('xOpa.savedAsText'), color: '' })));
      setSkuView({ doc: d, rows, loading: false });
    } catch (error) {
      setSkuView({ doc: d, rows: [], loading: false });
    }
  };

  /** Run `action`, or ask first when there is unsaved work. */
  const guard = (action) => { if (dirty) setLeaving(() => action); else action(); };

  /* What goes under each chart.
     `hardest` is a JUDGEMENT — the rows somebody ticked. On the team's own skin chart it is #21, #23
     and #24 at 130, 120 and 180 seconds while the three longest are 208, 200 and 180, so difficulty
     plainly is not duration and cannot be derived.
     `longest` IS derived: the team's entry there is #10, the 208-second bar, i.e. the argmax. Deriving
     it means it moves when an operation is re-timed — their CASE chart has the heading with nothing
     under it, which is what happens when a derived fact has to be copied by hand. */
  const bySection = useMemo(() => {
    const out = {};
    OPA_SECTIONS.forEach((sec) => {
      const own = lines.filter((l) => l.section === sec);
      const bars = own.map((l, i) => ({
        no: i + 1,
        secs: opaLineSeconds(l),
        stage: l.stageEn || l.stageVn || '',
        hard: !!l.isDifficult,
      }));
      const top = bars.reduce((best, b) => (best && best.secs >= b.secs ? best : b), null);
      out[sec] = {
        bars,
        hardest: bars.filter((b) => b.hard),
        longest: top && top.secs > 0 ? bars.filter((b) => b.secs === top.secs) : [],
      };
    });
    return out;
  }, [lines]);
  const sectionLines = lines.filter((l) => l.section === section);

  const loadBlocks = useCallback(async () => {
    try {
      const response = await fetch(`${apiBase}/opa/blocks`, { credentials: 'include' });
      if (response.ok) setBlocks((await response.json()).blocks || []);
    } catch (e) { /* the library is optional — the form works without it */ }
  }, [apiBase]);
  useEffect(() => { loadBlocks(); }, [loadBlocks]);

  /* The tick list is per section: switching tabs must not carry a selection onto rows nobody can
     see any more. */
  useEffect(() => { setPicked(new Set()); }, [section]);
  const togglePick = (i) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(i)) next.delete(i); else next.add(i);
    return next;
  });

  const storeBlock = async () => {
    setBlockErr('');
    const chosen = [...picked].sort((a, b) => a - b).map((i) => sectionLines[i]).filter(Boolean);
    try {
      const response = await fetch(`${apiBase}/opa/blocks`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...saveBlock, lines: chosen }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('xOpa.errSaveBlock'));
      setSaveBlock(null); setPicked(new Set());
      await loadBlocks();
    } catch (err) { setBlockErr(err.message); }
  };

  const removeBlock = async () => {
    setBlockErr('');
    const target = dropBlock;
    setDropBlock(null);
    try {
      const response = await fetch(`${apiBase}/opa/blocks/${target.id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!response.ok) throw new Error((await response.json()).error || t('xOpa.errRemove'));
      await loadBlocks();
    } catch (err) { setBlockErr(err.message); }
  };

  /* Insert = copy. The document owns its rows from then on: the numbers on a signed OPA were used to
     cost a product, and editing the standard later must not move them. */
  /* Matched on the name, the note, who saved it and every line of English and Vietnamese in it.
     A block whose operations match but whose name does not is exactly the one worth finding. */
  const shownBlocks = (() => {
    const q = blockSearch.trim().toLowerCase();
    if (!q) return blocks;
    return blocks.filter((b) => [
      b.name, b.note, b.createdBy,
      ...(b.lines || []).flatMap((l) => [l.stageEn, l.stageVn, l.needle, l.machine]),
    ].filter(Boolean).join(' ').toLowerCase().includes(q));
  })();

  /* Change what a block holds — Yen's 5. Both halves go through one PUT because both are "these are
     the operations now": removing sends the block's rows minus one, adding sends them plus the ones
     ticked in the document. `lines` is the whole list either way, so seq never has to be patched. */
  const updateBlock = async (block, lines) => {
    setBlockErr('');
    try {
      const response = await fetch(`${apiBase}/opa/blocks/${block.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lines }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || t('xOpa.errUpdateBlock'));
      await loadBlocks();
      /* The tick set indexes into a list that just changed length, so it is rebuilt rather than
         carried over — a stale index would tick the wrong operation. */
      setBlockPick(new Set(lines.map((_, i) => i)));
    } catch (err) { setBlockErr(err.message); }
  };

  /* The operations ticked in the DOCUMENT, appended to a block that already exists. Same source the
     "Save as a standard block" button reads, so what lands in the block is what is ticked on screen. */
  const appendPickedToBlock = (block) => {
    const chosen = sectionLines.filter((_, i) => picked.has(i));
    if (!chosen.length) return;
    updateBlock(block, [...(block.lines || []), ...chosen.map((l) => ({ ...l }))]);
  };

  const dropBlockLine = (block, index) =>
    updateBlock(block, (block.lines || []).filter((_, i) => i !== index));

  const openBlock = (b) => {
    const same = blockOpen === b.id;
    setBlockOpen(same ? null : b.id);
    setBlockPick(same ? null : new Set((b.lines || []).map((_, i) => i)));
  };
  const togglePickLine = (i) => setBlockPick((prev) => {
    const next = new Set(prev || []);
    if (next.has(i)) next.delete(i); else next.add(i);
    return next;
  });

  /* Insert the whole block, or only the ticked operations when it is open. A block of 12 where two
     apply is otherwise "insert all, then delete ten" (Max, 2026-09-03). */
  const insertBlock = (block) => {
    const all = block.lines || [];
    const keep = (blockOpen === block.id && blockPick)
      ? all.filter((_, i) => blockPick.has(i))
      : all;
    if (!keep.length) return;
    setLines((prev) => [...prev, ...keep.map((l) => ({ ...BLANK_LINE(section), ...l, section }))]);
    setInsertOpen(false);
    setBlockOpen(null); setBlockPick(null);
  };

  const editLine = (indexInSection, key, value) => setLines((prev) => {
    let seen = -1;
    return prev.map((l) => {
      if (l.section !== section) return l;
      seen += 1;
      return seen === indexInSection ? { ...l, [key]: value } : l;
    });
  });
  /* One hidden <input type=file> for the whole table rather than one per row: 27 of them is 27 DOM
     nodes and 27 refs to keep straight, and only one can ever be open. */
  const pickPhoto = (indexInSection) => {
    photoFor.current = indexInSection;
    if (photoInput.current) { photoInput.current.value = ''; photoInput.current.click(); }
  };

  /* The image is resized in the browser by the SAME helper the project photos use, then posted to the
     shared store under the `opaop` scope keyed by this document. Only the returned id is kept on the
     line. A document that has never been saved has no id yet, so its photos land under `draft` — the
     pointer is what matters, and the key is only which folder the file sits in. */
  /* One upload path for both ways in — the file picker and a paste. */
  const attachPhoto = async (file, at) => {
    if (!file || at === null || at === undefined) return;
    setPhotoBusy(at); setError('');
    try {
      const shrunk = await compressImage(file);
      const key = docId ? String(docId) : 'draft';
      const response = await fetch(`${apiBase}/development/photos/opaop/${encodeURIComponent(key)}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...shrunk, fileName: file.name || 'pasted.png' }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || t('xOpa.errPhoto'));
      if (!data.photoId) throw new Error(t('xOpa.errPhotoId'));
      editLine(at, 'photoId', data.photoId);
    } catch (err) { setError(err.message); } finally { setPhotoBusy(null); photoFor.current = null; }
  };

  const onPhotoFile = async (event) => {
    const file = event.target.files?.[0];
    const at = photoFor.current;
    event.target.value = '';
    await attachPhoto(file, at);
  };

  /* Ctrl+V straight onto a row (Max, 2026-09-03: cropping, saving and then browsing for the file is
     three steps for a screenshot that is already on the clipboard).

     WHICH row: the one holding the focused element. Every cell in the table is an input, so clicking
     anywhere in a row already means "this row" to the person doing it — no extra control to explain,
     and no guessing when the pointer happens to be elsewhere. `data-line` carries the index within
     the section, because that is what editLine takes.

     Bound to the operations table rather than the document: a paste into the Style name box at the
     top of the form is somebody pasting a style name, not a photo. */
  const onPasteInOps = async (event) => {
    if (readOnly) return;
    const items = [...(event.clipboardData?.items || [])];
    const img = items.find((i) => i.type && i.type.startsWith('image/'));
    if (!img) return;                       // a text paste is somebody typing, leave it alone
    const row = (document.activeElement || event.target)?.closest?.('tr[data-line]');
    if (!row) { setError(t('xOpa.pasteWhere')); return; }
    event.preventDefault();
    await attachPhoto(img.getAsFile(), Number(row.dataset.line));
  };

  /* Move one operation up or down within its section — Yen via Max, 2026-09-05 ("di chuyển lên
     xuống cho 1 công đoạn?"), and the reason her request 3 (insert a row above or below) is not
     being built: adding at the end and walking it into place does the same job with one control
     instead of two.

     WITHIN THE SECTION. `lines` holds every section in one array and the table shows a slice of it,
     so the two rows being swapped are found by their absolute position — swapping the slice would
     write it back in the wrong place. The order of the rows IS the operation order: opaSave numbers
     seq from the array, so nothing else has to be renumbered. */
  const moveLine = (indexInSection, delta) => setLines((prev) => {
    const at = [];
    prev.forEach((l, abs) => { if (l.section === section) at.push(abs); });
    const to = indexInSection + delta;
    if (to < 0 || to >= at.length) return prev;
    const next = [...prev];
    [next[at[indexInSection]], next[at[to]]] = [next[at[to]], next[at[indexInSection]]];
    return next;
  });

  const removeLine = (indexInSection) => setLines((prev) => {
    let seen = -1;
    return prev.filter((l) => {
      if (l.section !== section) return true;
      seen += 1;
      return seen !== indexInSection;
    });
  });

  const openNew = () => {
    setDocId(null); setDocNo(''); setHead(BLANK_HEAD); setLines([]);
    // A blank document is not "unsaved work" until something is typed into it, so the snapshot is
    // the blank state rather than empty — otherwise Back would nag on a form nobody touched.
    setSnapshot(JSON.stringify({ head: BLANK_HEAD, lines: [] }));
    setReadOnly(false); setSection('SKIN'); setError(''); setView('edit');
  };


  /** The chart SVG as an <img>, ready to draw onto a canvas. */
  const svgImage = (svg) => new Promise((resolve) => {
    const box = svg.getBoundingClientRect();
    const clone = svg.cloneNode(true);
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    clone.setAttribute('width', Math.max(1, Math.round(box.width)));
    clone.setAttribute('height', Math.max(1, Math.round(box.height)));
    const url = URL.createObjectURL(new Blob(
      [new XMLSerializer().serializeToString(clone)], { type: 'image/svg+xml' }));
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });

  /* One card as one PNG — the chart AND the two lists under it, framed the way the page frames
     them, because Max wants the export to carry the block exactly as the screen shows it
     (2026-08-30). Drawn by hand on a canvas: the chart part is the SVG rasterised, the notes are
     re-typeset with fillText. An HTML-to-image library would do this in one call and is exactly the
     kind of dependency that broke this project before, and the notes are five short lines. */
  const cardPng = async (box, minHeight = 0) => {
    const svg = box.querySelector('svg');
    if (!svg) return null;
    const img = await svgImage(svg);
    if (!img) return null;
    const S = 2;                                       // print resolution
    const W = Math.round(box.getBoundingClientRect().width);
    const chartRect = svg.getBoundingClientRect();
    const pad = 16; const titleH = 40; const lineH = 17;
    const title = (box.querySelector('h5')?.textContent || '').trim();

    // The notes, in document order, with the wrap measured before the canvas is sized.
    const items = [];
    box.querySelectorAll('.opa-notes > *').forEach((el) => {
      /* `data-png` carries the English of a translated note, so the picture stored with the document
         reads the same whoever saved it (the Excel and PDF exports are English). */
      if (el.tagName === 'STRONG') items.push({ text: (el.dataset.png || el.textContent).trim(), bold: true });
      else if (el.tagName === 'UL') {
        el.querySelectorAll('li').forEach((li) => items.push({
          text: `•  ${li.textContent.replace(/\s+/g, ' ').trim()}`, bold: false,
        }));
      } else if (el.tagName === 'P') items.push({ text: (el.dataset.png || el.textContent).trim(), bold: false, dim: true });
    });
    const measure = document.createElement('canvas').getContext('2d');
    const innerW = (W - pad * 2) * S;
    const wrapped = [];
    items.forEach((item) => {
      measure.font = `${item.bold ? '600 ' : ''}${12 * S}px system-ui, sans-serif`;
      const words = item.text.split(' ');
      let line = '';
      words.forEach((word) => {
        const probe = line ? `${line} ${word}` : word;
        if (measure.measureText(probe).width > innerW && line) {
          wrapped.push({ ...item, text: line });
          line = `   ${word}`;                          // hanging indent under the bullet
        } else line = probe;
      });
      if (line) wrapped.push({ ...item, text: line });
    });

    /* `minHeight` lets collectCharts render every card at the height of the tallest one, so the
       two pictures in the export come out the same size (Max, 2026-08-30) — CASE's notes run longer
       than SKIN's and the cards were otherwise unequal. The slack is white space inside the frame,
       under the notes, which is where the eye expects it. */
    const H = Math.max(minHeight,
      titleH + 8 + Math.round(chartRect.height) + 10 + wrapped.length * lineH + pad);
    const canvas = document.createElement('canvas');
    canvas.width = W * S; canvas.height = H * S;
    const ctx = canvas.getContext('2d');
    // the card: white, hairline border, grey title band — the page's own framing
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#e2e8f0'; ctx.lineWidth = S;
    ctx.strokeRect(S / 2, S / 2, canvas.width - S, canvas.height - S);
    ctx.fillStyle = '#f8fafc'; ctx.fillRect(S, S, canvas.width - 2 * S, titleH * S - S);
    ctx.strokeStyle = '#e2e8f0';
    ctx.beginPath(); ctx.moveTo(0, titleH * S); ctx.lineTo(canvas.width, titleH * S); ctx.stroke();
    ctx.fillStyle = '#64748b'; ctx.font = `600 ${13 * S}px system-ui, sans-serif`;
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(title, canvas.width / 2, (titleH * S) / 2);
    // the chart
    ctx.drawImage(img, ((W - chartRect.width) / 2) * S, (titleH + 8) * S,
      Math.round(chartRect.width) * S, Math.round(chartRect.height) * S);
    // the notes
    ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
    let y = titleH + 8 + Math.round(chartRect.height) + 10 + 13;
    wrapped.forEach((line) => {
      ctx.font = `${line.bold ? '600 ' : ''}${12 * S}px system-ui, sans-serif`;
      ctx.fillStyle = line.bold ? '#0f172a' : (line.dim ? '#94a3b8' : '#334155');
      ctx.fillText(line.text, pad * S, y * S);
      y += lineH;
    });
    return { dataBase64: canvas.toDataURL('image/png').replace(/^data:[^,]+,/, ''), width: W, height: H };
  };

  /* Every chart card on the page, rasterised with its true size so the export keeps the aspect
     ratio. Called from BOTH Save and the Excel button: Save stores them next to the data, which is
     what lets the list's plain-GET Excel link produce the same file as the button here — relying on
     the button alone is how Max got a workbook with an empty top-left corner (2026-08-29). */
  const collectCharts = async () => {
    const boxes = [...document.querySelectorAll('.opa-chart')];
    // First pass finds each card's natural height; the tallest sets the height for all of them.
    const first = [];
    for (const box of boxes) {
      // eslint-disable-next-line no-await-in-loop
      first.push(await cardPng(box));
    }
    const maxH = Math.max(0, ...first.filter(Boolean).map((c) => c.height));
    const charts = [];
    for (let i = 0; i < boxes.length; i += 1) {
      if (!first[i]) continue;
      const card = first[i].height === maxH
        ? first[i]
        // eslint-disable-next-line no-await-in-loop
        : await cardPng(boxes[i], maxH);
      charts.push({ section: boxes[i].dataset.section, ...card });
    }
    return charts;
  };

  const exportExcel = async () => {
    if (!docId) return;
    setBusy(true); setError('');
    try {
      const charts = await collectCharts();
      const response = await fetch(`${apiBase}/opa/${docId}/export-xlsx`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ charts }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || t('xOpa.errExport', { status: response.status }));
      }
      const blob = await response.blob();
      const name = (response.headers.get('Content-Disposition') || '')
        .match(/filename="([^"]+)"/)?.[1] || 'OPA.xlsx';
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = name;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };


  const openDoc = async (id, mode = 'view') => {
    setBusy(true); setError('');
    try {
      const response = await fetch(`${apiBase}/opa/${id}`, { credentials: 'include' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('xOpa.errOpen'));
      const d = data.doc;
      setDocId(d.id); setDocNo(d.docNo);
      const nextHead = {
        styleName: d.styleName || '', variant: d.variant || '', season: d.season || '',
        category: d.category || '', skus: d.skus || '',
        styleEfficiency: d.styleEfficiency, indirectRate: d.indirectRate,
        frameFitMin: d.frameFitMin, frameAssMin: d.frameAssMin, packagingMin: d.packagingMin,
        docDate: d.docDate ? String(d.docDate).slice(0, 10) : '', version: d.version,
        notes: d.notes || '',
        sectionTitles: d.sectionTitles || {},
        sectionMult: d.sectionMult || {},
      };
      setHead(nextHead);
      const nextLines = (data.lines || []).map((l) => ({
        section: l.section,
        stageEn: l.stage_en || '', stageVn: l.stage_vn || '',
        needle: l.needle || '', machine: l.machine || '',
        sewingLengthCm: l.sewing_length_cm ?? '', partQty: l.part_qty ?? '',
        ctPerPartSec: l.ct_per_part_sec ?? '', isDifficult: !!l.is_difficult,
        photoId: l.photo_id || null,
      }));
      setLines(nextLines);
      setSnapshot(JSON.stringify({ head: nextHead, lines: nextLines }));
      setReadOnly(mode === 'view');
      setSection('SKIN'); setView('edit');
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true); setError('');
    try {
      // The charts ride along and are stored with the document — see collectCharts.
      const charts = await collectCharts();
      const response = await fetch(`${apiBase}/opa${docId ? `/${docId}` : ''}`, {
        method: docId ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...head, lines, charts }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('xOpa.errSave'));
      setDocId(data.doc.id); setDocNo(data.doc.docNo);
      setSnapshot(nowState);
      await loadList();
      setView('list');
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    setConfirmDel(null); setBusy(true); setError('');
    try {
      const response = await fetch(`${apiBase}/opa/${id}`, { method: 'DELETE', credentials: 'include' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || t('xOpa.errDelete'));
      await loadList();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  /* Copy a document (Max, 2026-09-03). The season or the spec changes and the old costing has to
     stay readable, so the answer is a new document rather than an edit — the server copies the head
     and every line under a new handle with version + 1. It opens straight into the copy: nobody
     duplicates a document in order to look at the list again. */
  const duplicateDoc = async (d) => {
    setDupId(d.id); setError('');
    try {
      const response = await fetch(`${apiBase}/opa/${d.id}/duplicate`, {
        method: 'POST', credentials: 'include',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || t('xOpa.errCopy'));
      await loadList();
      if (data.doc?.id) await openDoc(data.doc.id, 'edit');
    } catch (err) { setError(err.message); } finally { setDupId(null); }
  };

  /* The file is read in the browser and posted as base64: there is no multer on this server and the
     rest of the portal already uploads this way. */
  const onFile = async (event) => {
    const f = event.target.files?.[0];
    event.target.value = '';
    if (!f) return;
    setBusy(true); setError(''); setImported(null);
    try {
      const buffer = await f.arrayBuffer();
      let binary = '';
      const bytes = new Uint8Array(buffer);
      for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
      const response = await fetch(`${apiBase}/opa/import-xlsx`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName: f.name, dataBase64: window.btoa(binary) }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('xOpa.errRead'));
      setImported({ fileName: f.name, sheets: data.sheets || [] });
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  /* One parsed sheet as the save endpoint wants it. Shared by the form path and the bulk path so the
     two cannot describe the same sheet differently. */
  const sheetPayload = (sheet) => ({
    styleName: sheet.styleName || '', season: sheet.season || '', skus: sheet.skus || '',
    styleEfficiency: sheet.styleEfficiency, indirectRate: sheet.indirectRate,
    frameFitMin: sheet.frameFitMin, frameAssMin: sheet.frameAssMin, packagingMin: sheet.packagingMin,
    docDate: sheet.docDate || new Date().toISOString().slice(0, 10),
    version: sheet.version || 1, notes: '',
    sectionTitles: sheet.sectionTitles || {},
    sectionMult: sheet.sectionMult || {},
    lines: (sheet.lines || []).map((l) => ({
      section: l.section,
      stageEn: l.stageEn || '', stageVn: l.stageVn || '',
      needle: l.needle || '', machine: l.machine || '',
      sewingLengthCm: l.sewingLengthCm ?? '', partQty: l.partQty ?? '',
      ctPerPartSec: l.ctPerPartSec ?? '', isDifficult: false,
    })),
  });

  /* Save every ticked sheet straight to the database (Max, 2026-09-03: "một file có thể có nhiều
     sheet ... tick chọn và lưu luôn, user sẽ tự kiểm tra lại sau"). A workbook of eight styles used to
     mean eight rounds of load-check-save.

     One POST per sheet rather than a bulk endpoint: this is the SAME create path a single save uses,
     so there is no second copy of the validation to drift from it. Sequential, because the doc_no is
     allocated inside each transaction and firing them in parallel only makes them queue anyway.

     A failure does not stop the rest — a workbook usually has one bad sheet, not eight, and stopping
     would leave the person guessing which ones landed. What failed is listed by name at the end.

     What these documents do NOT get is the chart pictures: those are captured from the rendered page
     at save time and there is no page here. The numbers are complete; the Excel export carries no
     chart until someone opens the document and saves it. */
  /* One way out, so a tick or a result from the last file never shows up against the next one. */
  const closeImport = () => { setImported(null); setPickedSheets(new Set()); setBulkResult(null); };

  const savePickedSheets = async () => {
    const sheets = (imported?.sheets || []).filter((sh) => pickedSheets.has(sh.sheet));
    if (!sheets.length) return;
    setBulkSave({ done: 0, total: sheets.length }); setBulkResult(null); setError('');
    const failed = [];
    let saved = 0;
    for (let i = 0; i < sheets.length; i += 1) {
      const sheet = sheets[i];
      try {
        // eslint-disable-next-line no-await-in-loop
        const response = await fetch(`${apiBase}/opa`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(sheetPayload(sheet)),
        });
        // eslint-disable-next-line no-await-in-loop
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || t('xOpa.errSaveStatus', { status: response.status }));
        saved += 1;
      } catch (err) {
        failed.push({ sheet: sheet.sheet, error: err.message });
      }
      setBulkSave({ done: i + 1, total: sheets.length });
    }
    setBulkSave(null);
    setBulkResult({ saved, failed });
    setPickedSheets(new Set());
    await loadList();
  };

  /* Loaded into the form, NOT saved. What came out of the file is on screen to be checked first. */
  const applySheet = (sheet) => {
    setDocId(null); setDocNo('');
    setHead({
      styleName: sheet.styleName || '', season: sheet.season || '', skus: sheet.skus || '',
      styleEfficiency: sheet.styleEfficiency, indirectRate: sheet.indirectRate,
      frameFitMin: sheet.frameFitMin, frameAssMin: sheet.frameAssMin,
      packagingMin: sheet.packagingMin,
      docDate: sheet.docDate || new Date().toISOString().slice(0, 10),
      version: sheet.version || 1, notes: '',
      sectionTitles: sheet.sectionTitles || {},
      sectionMult: sheet.sectionMult || {},
    });
    setLines((sheet.lines || []).map((l) => ({
      section: l.section,
      stageEn: l.stageEn || '', stageVn: l.stageVn || '',
      needle: l.needle || '', machine: l.machine || '',
      sewingLengthCm: l.sewingLengthCm ?? '', partQty: l.partQty ?? '',
      ctPerPartSec: l.ctPerPartSec ?? '', isDifficult: false,
    })));
    setImported(null); setSection('SKIN'); setView('edit');
    // An import IS unsaved work — it came out of a file and is not in the database yet.
    setSnapshot(''); setReadOnly(false);
  };

  return (
    <div className="opa">
      {!!error && <div className="sp-alert err">{error}</div>}

      {view === 'list' && (
        <>
          <div className="opa-tabs view-tabs" role="tablist">
            <button
              type="button"
              className={`opa-tab ${tab === 'docs' ? 'on' : ''}`}
              onClick={() => setTab('docs')}
            >
              <TabIcon k="docs" />{t('xOpa.tabDocs')}
            </button>
            <button
              type="button"
              className={`opa-tab ${tab === 'summary' ? 'on' : ''}`}
              onClick={() => setTab('summary')}
            >
              <TabIcon k="summary" />{t('xOpa.tabSummary')}
            </button>
            {/* Jay via Max, 2026-10-18 (the DAC board's "SMV library · automatic check"): every order's
                cycle time against what Daily Output says the line spent. */}
            <button
              type="button"
              className={`opa-tab ${tab === 'actual' ? 'on' : ''}`}
              onClick={() => setTab('actual')}
            >
              <TabIcon k="actual" />{t('xOpa.tabActual')}
            </button>
          </div>
          {tab === 'summary' && (
            <SctSummary apiBase={apiBase} onOpenDoc={(id) => openDoc(id, 'view')} />
          )}
          {tab === 'actual' && <SctActual apiBase={apiBase} />}
        </>
      )}

      {view === 'list' && tab === 'docs' && (
        <>
          <div className="opa-bar">
            <button type="button" className="sp-btn primary" onClick={openNew}>
              <Plus size={14} /> {t('xOpa.newOpa')}
            </button>
            <label className="sp-btn opa-file">
              <Upload size={14} /> {t('xOpa.importXlsx')}
              <input type="file" accept=".xlsx" onChange={onFile} />
            </label>
            <input
              className="sp-input opa-search"
              type="search"
              placeholder={t('xOpa.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {/* Clearing filters one column menu at a time is the only way otherwise, and after three
                or four columns nobody remembers which ones are on (Max, 2026-08-30). */}
            {listNarrowed && (
              <button type="button" className="sp-btn opa-clear" onClick={clearList}>
                <FilterX size={14} /> {t('xOpa.clear')}
              </button>
            )}
            <span className="opa-count">
              {shown.length === docs.length ? t('xOpa.nSaved', { n: docs.length })
                : t('xOpa.nOfM', { n: shown.length, total: docs.length })}
            </span>
          </div>

          {/* Fifteen rows and then a scrollbar, rather than a page that runs on for 149 (Max,
              2026-08-30). The header is already sticky; the box gives it something to stick to. */}
          <div className="opa-scroll">
          <table className="sp-table opa-list" ref={listRef}>
            {/* A colgroup, because the shared .sticky-col carries `max-width: 0` — the trick that
                makes a cell honour its COLUMN's width instead of growing to fit. Without a column
                width to honour, those cells collapsed to their padding (Max, 2026-08-30). The rest
                of the columns stay automatic. */}
            <colgroup>
              <col style={{ width: 46 }} />
              {LIST_COLUMNS.map((c) => {
                const w = FROZEN[c.key] || LIST_WIDTH[c.key];
                return <col key={c.key} style={w ? { width: w } : undefined} />;
              })}
              <col />
            </colgroup>
            <thead>
              <tr>
                {/* Not a sortable column: it numbers what is ON SCREEN, so it renumbers with the
                    sort and the filter rather than carrying an order of its own. */}
                <th className="sticky-col opa-no" style={{ left: frozenLeft[0] }}>{t('xOpa.no')}</th>
                {LIST_COLUMNS.map((c) => <SortTh key={c.key} {...listTh(c)} />)}
                <th className="sticky-right">{t('xOpa.action')}</th>
              </tr>
            </thead>
            <tbody>
              {shown.map(({ doc: d }, i) => (
                <tr key={d.id}>
                  <td className="num sticky-col opa-no" style={{ left: frozenLeft[0] }}>{i + 1}</td>
                  <td className="sticky-col" style={{ left: frozenLeft[1] }}>{d.docNo}</td>
                  <td className="opa-style sticky-col" style={{ left: frozenLeft[2] }}>
                    <strong>{d.styleName || '—'}</strong>
                    {/* The colour, phase or counter sheet this document is for. It used to be glued
                        onto the style name; Yến's point is that it is not part of the name. */}
                    {!!d.variant && <small className="opa-variant">{d.variant}</small>}
                  </td>
                  <td className="sticky-col last-sticky" style={{ left: frozenLeft[3] }}>
                    {d.season || '—'}
                  </td>
                  <td>{d.line || '—'}</td>
                  <td>{d.plCategory || '—'}</td>
                  <td>{d.category || '—'}</td>
                  <td className="mid">
                    {skuCell(d.skus).count ? (
                      <button
                        type="button"
                        className="sp-mini-btn"
                        title={t('xOpa.showSkus')}
                        onClick={() => openSkuView(d)}
                      >
                        <List size={12} /> {skuCell(d.skus).count}
                      </button>
                    ) : '—'}
                  </td>
                  <td className="num opa-sct">{n2(d.totals.sct)}</td>
                  <td className="num">{n2(d.totals.hours.SKIN)}</td>
                  <td className="num">{n2(d.totals.hours.CASE)}</td>
                  <td className="num">{n2(accHours(d))}</td>
                  <td className="num">{d.version}</td>
                  <td>{d.createdAt ? String(d.createdAt).slice(0, 10) : '—'}</td>
                  <td className="opa-who">{d.createdBy || '—'}</td>
                  <td>{d.updatedAt ? String(d.updatedAt).slice(0, 10) : '—'}</td>
                  <td className="opa-who">{d.updatedBy || '—'}</td>
                  <td className="opa-act sticky-right">
                    {/* Icons alone (Max, 2026-08-30): labelled buttons per row cost more width than
                        the numbers they sit beside. The title is what a hover asks for. Excel and
                        PDF kept their words until 2026-09-03, when Duplicate made a sixth button and
                        Max asked for the column back; they are a spreadsheet and a document glyph
                        now, which is also why they no longer share one FileDown. */}
                    <button
                      type="button"
                      className="sp-mini-btn opa-icon"
                      title={t('xOpa.view')}
                      onClick={() => openDoc(d.id, 'view')}
                    >
                      <Eye size={13} />
                    </button>
                    <button
                      type="button"
                      className="sp-mini-btn opa-icon"
                      title={t('xOpa.edit')}
                      onClick={() => openDoc(d.id, 'edit')}
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      type="button"
                      className="sp-mini-btn opa-icon"
                      title={t('xOpa.dupTitle')}
                      disabled={dupId === d.id}
                      onClick={() => duplicateDoc(d)}
                    >
                      <Copy size={13} />
                    </button>
                    <a
                      className="sp-mini-btn opa-icon"
                      title={t('xOpa.exportExcel')}
                      href={`${apiBase}/opa/${d.id}/export-xlsx`}
                    >
                      <FileSpreadsheet size={13} />
                    </a>
                    <a
                      className="sp-mini-btn opa-icon"
                      title={t('xOpa.exportPdf')}
                      href={`${apiBase}/opa/${d.id}/export-pdf`}
                    >
                      <FileText size={13} />
                    </a>
                    <button
                      type="button"
                      className="sp-mini-btn sp-mini-danger opa-icon"
                      title={t('xOpa.delete')}
                      onClick={() => setConfirmDel(d)}
                    >
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
              {/* No + the columns + Action. Counted, not typed: it was already one short before
                  four more columns were added on 2026-09-03. */}
              {!docs.length && (
                <tr><td className="sp-empty" colSpan={LIST_COLUMNS.length + 2}>{t('xOpa.nothingSaved')}</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </>
      )}

      {view === 'edit' && (
        <>
          <div className="opa-bar">
            <button
              type="button"
              className="sp-btn"
              onClick={() => guard(() => { setView('list'); setError(''); setSnapshot(''); })}
            >
              <ArrowLeft size={14} /> {t('xOpa.back')}
            </button>
            {!!docNo && <span className="opa-docno">{docNo}</span>}
            {readOnly && <span className="opa-ro-tag"><Eye size={12} /> {t('xOpa.viewOnly')}</span>}
            {dirty && <span className="opa-dirty">{t('xOpa.unsaved')}</span>}
            {readOnly ? (
              <button type="button" className="sp-btn primary" onClick={() => setReadOnly(false)}>
                <Pencil size={14} /> {t('xOpa.edit')}
              </button>
            ) : (
              <button type="button" className="sp-btn primary" disabled={busy} onClick={save}>
                <Save size={14} /> {docId ? t('xOpa.saveChanges') : t('xOpa.save')}
              </button>
            )}
            {!!docId && (
              <button type="button" className="sp-btn" disabled={busy} onClick={exportExcel}>
                <FileDown size={14} /> {busy ? t('xOpa.building') : 'Excel'}
              </button>
            )}
            {/* A plain link: the PDF draws from the charts stored at the last save, so unlike the
                Excel button it has nothing to collect from the page first. */}
            {!!docId && (
              <a className="sp-btn" href={`${apiBase}/opa/${docId}/export-pdf`}>
                <FileDown size={14} /> PDF
              </a>
            )}
          </div>

          {/* A disabled <fieldset> turns off every control inside it — real form semantics, one
              attribute, and it cannot fall out of step the way `disabled={readOnly}` repeated on
              forty inputs would. CSS-only read-only was the other option and is not one: it looks
              the same and still lets a keyboard reach the fields. */}
          <fieldset className="opa-fieldset" disabled={readOnly}>
          <div className="opa-top">
            <div className="opa-fields">
              <label><span>{t('xOpa.fStyleName')}</span>
                <input className="sp-input" value={head.styleName} onChange={setField('styleName')} /></label>
              {/* The colour, phase or counter sheet — a second document under the same style. It is
                  not part of the style's name (Yến via Max, 2026-09-01), so it has its own box. */}
              <label><span>{t('xOpa.fVariant')}</span>
                <input
                  className="sp-input"
                  value={head.variant}
                  onChange={setField('variant')}
                  placeholder="Coyote Tan, P2, counter…"
                /></label>
              {/* Max, 2026-09-03. A fixed list rather than a free-text box, because it is what the
                  documents get grouped and filtered by. An imported or older document has none, and
                  the blank option is how it stays that way instead of being silently assigned one. */}
              <label><span>{t('xOpa.fCategory')}</span>
                <select className="sp-input" value={head.category || ''} onChange={setField('category')}>
                  <option value="">—</option>
                  {OPA_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select></label>
              <label><span>{t('xOpa.fSeason')}</span>
                <input className="sp-input" value={head.season} onChange={setField('season')} /></label>
              <label className="opa-skus"><span>{t('xOpa.fSkus')}</span>
                {/* Chips plus a picker, NOT a free-text box: the names have to match the style master
                    or nobody can ever join on them. Old documents that carry typed text still render —
                    the string is split on commas, whatever wrote it. */}
                {/* A SUMMARY, not the whole pile: five chips wrapped this field to four lines and
                    shoved it over its neighbours (Max, 2026-08-30, "UI bị xấu khi chọn nhiều").
                    Two chips say what kind of thing is picked; the count says how many; everything
                    is managed inside the picker, which has the room. */}
                <div className={`sp-input opa-sku-box ${readOnly ? 'ro' : ''}`}>
                  {skuList.slice(0, 2).map((token) => (
                    <span className="opa-sku-chip" key={token}>{token}</span>
                  ))}
                  {skuList.length > 2 && <span className="opa-sku-chip more">+{skuList.length - 2}</span>}
                  {!skuList.length && <em>—</em>}
                  {!readOnly && (
                    <button
                      type="button"
                      className="opa-sku-add"
                      onClick={(e) => {
                        e.preventDefault(); setSkuOpen((v) => !v); setSkuSearch(''); setSkuShowPicked(false);
                      }}
                    >
                      {skuList.length ? t('xOpa.edit') : t('xOpa.skuAdd')}
                    </button>
                  )}
                </div>
              </label>
                {skuOpen && !readOnly && (
                  /* A modal with a TABLE, not a dropdown (Max, 2026-08-30) — and a style already
                     claimed by another OPA is disabled with the claimant named, because one style
                     has one cycle-time document and a second claim would leave costing guessing
                     which SCT is real. A name picked on THIS document stays tickable so it can be
                     unticked.

                     It closes on X and Done ONLY. It sat inside the SKUs <label> at first, and a
                     click anywhere in a label re-dispatches onto the label's first form control —
                     here the Edit/+Add TOGGLE — so clicking blank space inside the modal shut it
                     (Max, 2026-08-30). It renders after the label now, and the backdrop does not
                     close either: a stray click into a 200-row pick must never throw the picks. */
                  <div className="sp-modal-back">
                    <div className="sp-modal opa-skubox" onClick={(e) => e.stopPropagation()}>
                      <div className="sp-modal-h">
                        <div>
                          <strong>{t('xOpa.pickTitle')}</strong>
                          <small>{docNo || t('xOpa.newOpa')}</small>
                        </div>
                        <button type="button" className="sp-icon-btn" onClick={() => setSkuOpen(false)}>
                          <X size={16} />
                        </button>
                      </div>
                      <div className="sp-modal-body">
                        <div className="opa-sku-toolbar">
                          <input
                            className="sp-input"
                            placeholder={t('xOpa.skuSearch')}
                            value={skuSearch}
                            autoFocus
                            disabled={skuShowPicked}
                            onChange={(e) => setSkuSearch(e.target.value)}
                          />
                          {/* The quick look at what is already ticked (Max, 2026-08-30). A toggle,
                              not a separate screen: the same table, fed the picked codes instead of
                              the search. */}
                          <button
                            type="button"
                            className={`sp-btn ${skuShowPicked ? 'primary' : ''}`}
                            disabled={!skuList.length && !skuShowPicked}
                            onClick={() => setSkuShowPicked((v) => !v)}
                          >
                            <Eye size={14} /> {t('xOpa.picked', { n: skuList.length })}
                          </button>
                        </div>
                        {/* FERT materials only, one row per sellable SKU. What is STORED is the SAP
                            code — the one identity every other system here joins on. */}
                        <div className="opa-sku-scroll">
                          <table className="sp-table opa-sku-table">
                            <thead>
                              <tr>
                                <th /><th>{t('xOpa.hSku')}</th><th>{t('xOpa.hSap')}</th><th>{t('xOpa.hStyle')}</th>
                                <th>{t('xOpa.hColor')}</th><th>{t('xOpa.hUsedBy')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {skuNames.map(({ sap, sku, name, color, usedBy }) => {
                                const picked = skuList.includes(sap);
                                const locked = !!usedBy && !picked;
                                return (
                                  <tr key={sap} className={locked ? 'locked' : ''}>
                                    <td className="mid">
                                      <input
                                        type="checkbox"
                                        checked={picked}
                                        disabled={locked}
                                        onChange={(e) => setSkuList(e.target.checked
                                          ? [...skuList, sap]
                                          : skuList.filter((v) => v !== sap))}
                                      />
                                    </td>
                                    <td className="opa-sku-code">{sku || '—'}</td>
                                    <td className="opa-sku-code">{sap}</td>
                                    <td>{name || '—'}</td>
                                    <td>{color || '—'}</td>
                                    <td>{usedBy ? <span className="opa-usedby">{usedBy}</span> : '—'}</td>
                                  </tr>
                                );
                              })}
                              {!skuNames.length && (
                                <tr>
                                  <td className="sp-empty" colSpan={6}>
                                    {skuLoading ? t('xOpa.searching')
                                      : (skuShowPicked ? t('xOpa.nothingPicked') : t('xOpa.noFert'))}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                        <p className="opa-notes-empty">
                          {t('xOpa.fertNote')}
                        </p>
                      </div>
                      <div className="sp-modal-f">
                        <span className="opa-sku-count">
                          {skuShowPicked ? t('xOpa.nPickedShowing', { n: skuList.length }) : t('xOpa.nPicked', { n: skuList.length })}
                        </span>
                        <button type="button" className="sp-btn primary" onClick={() => setSkuOpen(false)}>
                          {t('xOpa.done')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              {/* Typed as a percentage — 74, not 0.74 (Max, 2026-09-04). The factor is what the
                  maths, the workbook, the export and the database all use, so only the box changes:
                  it shows the factor times a hundred and stores what is typed divided by it. */}
              <label><span>{t('xOpa.fEff')}</span>
                <span className="opa-unit-box">
                  <input className="sp-input" type="number" step="0.1"
                    value={effShown} onChange={onEffChange} onBlur={() => setEffTyping(null)} />
                  <i>%</i>
                </span></label>
              <label><span>{t('xOpa.fIndirect')}</span>
                <input className="sp-input" type="number" step="0.01"
                  value={head.indirectRate} onChange={setField('indirectRate')} />
                {!readOnly && <em>{t('xOpa.indirectHint')}</em>}</label>
              {/* Two decimals (Max, 2026-09-03). step="0.1" made the browser mark 1.25 invalid, and
                  these are the minutes the whole standard-work half is built from — the column is
                  decimal(9,3), so the storage was never the limit. */}
              <label><span>{t('xOpa.fFrameFit')}</span>
                <input className="sp-input" type="number" step="0.01"
                  value={head.frameFitMin} onChange={setField('frameFitMin')} /></label>
              <label><span>{t('xOpa.fFrameAss')}</span>
                <input className="sp-input" type="number" step="0.01"
                  value={head.frameAssMin} onChange={setField('frameAssMin')} /></label>
              <label><span>{t('xOpa.fPackaging')}</span>
                <input className="sp-input" type="number" step="0.01"
                  value={head.packagingMin} onChange={setField('packagingMin')} /></label>
              <label><span>{t('xOpa.fDate')}</span>
                <input className="sp-input" type="date" value={head.docDate || ''}
                  onChange={setField('docDate')} /></label>
              <label><span>{t('xOpa.fVersion')}</span>
                <input className="sp-input" type="number" value={head.version}
                  onChange={setField('version')} /></label>
            </div>

            {/* The header block from the workbook, live. Same order, same labels, so somebody holding
                the spreadsheet can read straight across. */}
            {/* Laid out like the block in the team's own workbook (Max, 2026-09-03, screenshot):
                ruled rows, the two subtotals in red against blue labels, the parts under them in a
                lighter blue, and (h) beside every figure at the same size as the number rather than
                a small grey suffix — this is a costing sheet, and the unit is part of the figure.

                `.grp` is a subtotal row, `.sub` a part of one. Both were already the shape here; only
                the paint changed. */}
            <div className="opa-sum">
              <div className="opa-sum-h"><span>SCT</span><b>{n2(totals.sct)}<i>(h)</i></b></div>
              <div className="opa-sum-r grp"><span>{t('xOpa.sumVar')}</span><b>{n2(totals.variable)}<i>(h)</i></b></div>
              {OPA_SECTIONS.map((s) => (
                <div className="opa-sum-r sub" key={s}>
                  <span>{t('xOpa.secSecs', { name: secShort(s), n: int(seconds[s]) })}</span>
                  <b>{n2(totals.hours[s])}<i>(h)</i></b>
                </div>
              ))}
              <div className="opa-sum-r grp"><span>{t('xOpa.sumStd')}</span><b>{n2(totals.standard)}<i>(h)</i></b></div>
              <div className="opa-sum-r sub"><span>{t('xOpa.sumFrameFit')}</span><b>{n2(totals.frameFit)}<i>(h)</i></b></div>
              <div className="opa-sum-r sub"><span>{t('xOpa.sumFrameAss')}</span><b>{n2(totals.frameAss)}<i>(h)</i></b></div>
              <div className="opa-sum-r sub"><span>{t('xOpa.sumPackaging')}</span><b>{n2(totals.packaging)}<i>(h)</i></b></div>
            </div>
          </div>

          {/* OUTSIDE the fieldset. They are navigation, and a disabled fieldset disables every
              control inside it — which in view mode left CASE and ACC unreachable, so a viewer could
              only ever see SKIN (Max, 2026-08-29). Nothing that only changes what you are looking at
              belongs inside the block that turns editing off. */}
          </fieldset>
          <div className="opa-sections">
            {OPA_SECTIONS.map((s) => (
              <button
                key={s}
                type="button"
                className={`opa-sec ${section === s ? 'on' : ''}`}
                onClick={() => setSection(s)}
              >
                {secLabel(s)}
                <em>{t('xOpa.opsSecs', { n: lines.filter((l) => l.section === s).length, s: int(seconds[s]) })}</em>
              </button>
            ))}
          </div>

          <fieldset className="opa-fieldset" disabled={readOnly}>
          <div className="opa-tablewrap">
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
            <table className="sp-table opa-ops" onPaste={onPasteInOps}>
              <colgroup>
                <col className="c-chk" />
                <col className="c-no" /><col /><col /><col className="c-nd" /><col className="c-mc" />
                <col className="c-sm" /><col className="c-sm" /><col className="c-sm" />
                <col className="c-sm" /><col className="c-chk" /><col className="c-photo" />
                <col className="c-del" />
              </colgroup>
              <thead>
                <tr>
                  {/* Tick the rows that make up a standard block. */}
                  <th className="mid c-chk" title={t('xOpa.pickForBlock')} />
                  <th>{t('xOpa.hNo')}</th><th>{t('xOpa.hStages')}</th><th>{t('xOpa.hStagesVn')}</th><th>{t('xOpa.hNeedle')}</th>
                  <th>{t('xOpa.hMachine')}</th><th className="num">{t('xOpa.hLength')}</th>
                  <th className="num">{t('xOpa.hPartQty')}</th><th className="num">{t('xOpa.hCtPart')}</th>
                  <th className="num">{t('xOpa.hCt')}</th>
                  <th className="mid" title={t('xOpa.hardTitle')}>{t('xOpa.hHard')}</th>
                  <th className="mid" title={t('xOpa.photoTitle')}>{t('xOpa.hPhoto')}</th><th />
                </tr>
              </thead>
              <tbody>
                {sectionLines.map((l, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={i} data-line={i} className={picked.has(i) ? 'opa-picked' : ''}>
                    <td className="mid">
                      <input
                        type="checkbox"
                        checked={picked.has(i)}
                        onChange={() => togglePick(i)}
                        aria-label={t('xOpa.pickOp', { n: i + 1 })}
                      />
                    </td>
                    <td className="sp-rownum">{i + 1}</td>
                    <td><input className="sp-input" value={l.stageEn}
                      onChange={(e) => editLine(i, 'stageEn', e.target.value)} /></td>
                    <td><input className="sp-input" value={l.stageVn}
                      onChange={(e) => editLine(i, 'stageVn', e.target.value)} /></td>
                    <td><input className="sp-input" value={l.needle}
                      onChange={(e) => editLine(i, 'needle', e.target.value)} /></td>
                    <td><input className="sp-input" value={l.machine}
                      onChange={(e) => editLine(i, 'machine', e.target.value)} /></td>
                    <td><input className="sp-input num" type="number" step="0.1" value={l.sewingLengthCm}
                      onChange={(e) => editLine(i, 'sewingLengthCm', e.target.value)} /></td>
                    <td><input className="sp-input num" type="number" step="1" value={l.partQty}
                      onChange={(e) => editLine(i, 'partQty', e.target.value)} /></td>
                    <td><input className="sp-input num" type="number" step="1" value={l.ctPerPartSec}
                      onChange={(e) => editLine(i, 'ctPerPartSec', e.target.value)} /></td>
                    {/* Not an input: column J of the workbook is qty x seconds, never typed. */}
                    <td className="num opa-calc">{int(opaLineSeconds(l))}</td>
                    {/* "Most difficult process" is ticked here rather than typed under the chart, so
                        the (#21) in the list keeps pointing at the same stitch when rows move. */}
                    <td className="mid">
                      <input
                        type="checkbox"
                        checked={!!l.isDifficult}
                        title={t('xOpa.hardTitle')}
                        onChange={(e) => editLine(i, 'isDifficult', e.target.checked)}
                      />
                    </td>
                    {/* The thumbnail IS the control: click to see it full size, and the × on top of
                        it unlinks the row. Unlink, not delete — the same image may already have been
                        carried into a standard block, and the file is shared. */}
                    <td className="mid opa-photocell">
                      {l.photoId ? (
                        <span className="opa-thumbwrap">
                          <button
                            type="button"
                            className="opa-thumb"
                            title={t('xOpa.viewPhoto')}
                            onClick={() => setLightbox(l.photoId)}
                          >
                            <img src={PHOTO_URL(l.photoId)} alt={l.stageEn || t('xOpa.opN', { n: i + 1 })} />
                          </button>
                          {!readOnly && (
                            <button
                              type="button"
                              className="opa-thumb-x"
                              title={t('xOpa.removePhoto')}
                              onClick={() => editLine(i, 'photoId', null)}
                            >
                              <X size={10} />
                            </button>
                          )}
                        </span>
                      ) : (
                        !readOnly && (
                          <button
                            type="button"
                            className="sp-mini-btn opa-icon"
                            title={t('xOpa.addPhoto')}
                            disabled={photoBusy === i}
                            onClick={() => pickPhoto(i)}
                          >
                            <ImagePlus size={13} />
                          </button>
                        )
                      )}
                    </td>
                    <td className="mid opa-rowact">
                      {!readOnly && (
                        <>
                          <button
                            type="button" className="sp-mini-btn opa-icon" title={t('xOpa.moveUp')}
                            disabled={i === 0}
                            onClick={() => moveLine(i, -1)}
                          ><ChevronUp size={12} /></button>
                          <button
                            type="button" className="sp-mini-btn opa-icon" title={t('xOpa.moveDown')}
                            disabled={i === sectionLines.length - 1}
                            onClick={() => moveLine(i, 1)}
                          ><ChevronDown size={12} /></button>
                          <button type="button" className="sp-mini-btn sp-mini-danger" title={t('xOpa.removeOp')}
                            onClick={() => removeLine(i)}><Trash2 size={12} /></button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {!sectionLines.length && (
                  <tr><td className="sp-empty" colSpan={13}>
                    {t('xOpa.noOpsIn', { sec: secLabel(section) })}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* One picker for the whole table — only one row can be uploading at a time. Outside the
              fieldset would be tidier but it is display:none either way, and inside keeps it next to
              the table it belongs to. */}
          <input
            ref={photoInput}
            type="file"
            accept="image/jpeg,image/png"
            style={{ display: 'none' }}
            onChange={onPhotoFile}
          />

          {/* Nobody discovers a paste handler on their own. */}
          {!readOnly && (
            <p className="opa-paste-hint">
              {fillParts(t('xOpa.pasteHint', { keys: marker('keys') }), { keys: <><kbd>Ctrl</kbd><kbd>V</kbd></> })}
            </p>
          )}

          </fieldset>

          {!readOnly && (
            <div className="opa-bar">
              <button
                type="button"
                className="sp-btn"
                onClick={() => setLines((prev) => [...prev, BLANK_LINE(section)])}
              >
                <Plus size={14} /> {t('xOpa.addOpTo', { sec: secLabel(section) })}
              </button>
              {/* The library. Saving works on the ticked rows; inserting appends a copy. */}
              <button
                type="button"
                className="sp-btn"
                disabled={!picked.size}
                title={picked.size ? '' : t('xOpa.tickFirst')}
                onClick={() => { setBlockErr(''); setSaveBlock({ name: '', note: '' }); }}
              >
                <BookmarkPlus size={14} /> {picked.size ? t('xOpa.saveAsBlockN', { n: picked.size }) : t('xOpa.saveAsBlock')}
              </button>
              <button
                type="button"
                className="sp-btn"
                disabled={!blocks.length}
                title={blocks.length ? '' : t('xOpa.noBlockYet')}
                onClick={() => setInsertOpen(true)}
              >
                <Library size={14} /> {t('xOpa.insertBlock')}
              </button>
            </div>
          )}

          {/* The chart page. Laid out the way the team draws it: one chart per section that has
              operations, two across, with the two lists underneath each. */}
          <div className="opa-charts">
            {OPA_SECTIONS.filter((sec) => bySection[sec].bars.length).map((sec) => (
              <div className="opa-chart" data-section={sec} key={sec}>
                <h5>CT — {secShort(sec)}</h5>
                <div className="opa-chart-box">
                  <ResponsiveContainer width="100%" height={230}>
                    <BarChart
                      data={bySection[sec].bars}
                      margin={{ top: 22, right: 8, bottom: 4, left: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(15,23,42,0.10)" />
                      <XAxis dataKey="no" tick={{ fontSize: 10 }} interval={0} />
                      <YAxis tick={{ fontSize: 10 }} width={38} />
                      <Bar dataKey="secs" fill={SECTION_COLOUR[sec]} isAnimationActive={false}>
                        {/* The number on top of the bar, as on the team's chart. It is what somebody
                            reads off the picture, and a tooltip would hide it behind a hover. */}
                        <LabelList dataKey="secs" position="top" style={{ fontSize: 10, fill: '#475569' }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="opa-notes">
                  <strong data-png="Most difficult process:">{t('xOpa.mostDifficult')}</strong>
                  {bySection[sec].hardest.length ? (
                    <ul>
                      {bySection[sec].hardest.map((b) => (
                        <li key={b.no}>{b.stage || '—'} <span>(#{b.no})</span></li>
                      ))}
                    </ul>
                  ) : (
                    <p className="opa-notes-empty" data-png="Tick Hard on the rows that are difficult.">
                      {fillParts(t('xOpa.tickHard', { hard: marker('hard') }), { hard: <b>{t('xOpa.hHard')}</b> })}
                    </p>
                  )}
                  <strong data-png="Max take time process:">{t('xOpa.maxTake')}</strong>
                  {bySection[sec].longest.length ? (
                    <ul>
                      {bySection[sec].longest.map((b) => (
                        <li key={b.no}>
                          {b.stage || '—'} <span>(#{b.no})</span> <b>{int(b.secs)} sec</b>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="opa-notes-empty" data-png="No timed operation yet.">{t('xOpa.noTimed')}</p>
                  )}
                </div>
              </div>
            ))}
            {!OPA_SECTIONS.some((sec) => bySection[sec].bars.length) && (
              <p className="sp-note">{t('xOpa.chartsAppear')}</p>
            )}
          </div>

          {/* The product photos, using the SAME component as the Project Tracker (Max, 2026-08-29:
              "chuyển ảnh bạn nên làm giống bên project tracker"). It already carries the upload, the
              list, the delete and the full-size viewer with its arrows, captions and Esc — four
              things this tab had reimplemented slightly differently, and worse. The store has taken
              an 'opa' scope since yesterday, so the component needed nothing but a scope and a key. */}
          <div className="opa-photos">
            {docId ? (
              <ProjectPhotos scope="opa" ownerKey={String(docId)} readOnly={readOnly} />
            ) : (
              <div className="opa-photos-h">
                <strong>{t('xOpa.photos')}</strong>
                <span className="opa-notes-empty">
                  {t('xOpa.saveFirst')}
                </span>
              </div>
            )}
          </div>
        </>
      )}

      {!!imported && (
        <div className="sp-modal-back" onClick={closeImport}>
          <div className="sp-modal opa-importbox" onClick={(e) => e.stopPropagation()}>
            <div className="sp-modal-h">
              <div>
                <strong>{t('xOpa.importTitle')}</strong>
                <small>{imported.fileName}</small>
              </div>
              <button type="button" className="sp-icon-btn" onClick={closeImport}>
                <X size={16} />
              </button>
            </div>
            <div className="sp-modal-body">
              <p className="sp-note">
                {t('xOpa.importNote')}
              </p>
              {imported.sheets.map((sheet) => {
                const secs = opaSeconds(sheet.lines);
                const tot = opaTotals(sheet, secs);
                const on = pickedSheets.has(sheet.sheet);
                return (
                  <div key={sheet.sheet} className={`opa-sheet-row ${on ? 'on' : ''}`}>
                    {/* The tick is its own control beside the row, not on it: clicking the row still
                        means "open this one", which is what the dialog did before and what a single
                        sheet still wants. */}
                    <label className="opa-sheet-tick">
                      <input
                        type="checkbox"
                        checked={on}
                        disabled={!!bulkSave}
                        onChange={() => setPickedSheets((prev) => {
                          const next = new Set(prev);
                          if (next.has(sheet.sheet)) next.delete(sheet.sheet);
                          else next.add(sheet.sheet);
                          return next;
                        })}
                        aria-label={t('xOpa.selectSheet', { sheet: sheet.sheet })}
                      />
                    </label>
                    <button
                      type="button"
                      className="opa-sheet"
                      disabled={!!bulkSave}
                      onClick={() => applySheet(sheet)}
                    >
                      <span className="nm">{sheet.sheet}</span>
                      <span className="dt">
                        {t('xOpa.sheetMeta', { style: sheet.styleName, season: sheet.season || t('xOpa.noSeason'), n: sheet.lines.length })}
                      </span>
                      <span className="sc">SCT {n2(tot.sct)}</span>
                    </button>
                  </div>
                );
              })}
              {!imported.sheets.length && <p className="sp-note">{t('xOpa.noOpaSheet')}</p>}

              {!!bulkResult && (
                <div className={`sp-alert ${bulkResult.failed.length ? 'warn' : 'ok'}`}>
                  {bulkResult.saved > 0 && (
                    <div>
                      {t('xOpa.bulkSaved', { count: bulkResult.saved })}
                    </div>
                  )}
                  {bulkResult.failed.map((f) => (
                    <div key={f.sheet}><strong>{f.sheet}</strong>: {f.error}</div>
                  ))}
                </div>
              )}
            </div>
            {imported.sheets.length > 1 && (
              <div className="sp-modal-f opa-import-f">
                <label className="opa-sheet-all">
                  <input
                    type="checkbox"
                    disabled={!!bulkSave}
                    checked={pickedSheets.size > 0 && pickedSheets.size === imported.sheets.length}
                    onChange={(e) => setPickedSheets(e.target.checked
                      ? new Set(imported.sheets.map((sh) => sh.sheet)) : new Set())}
                  />
                  {t('xOpa.allSheets', { n: imported.sheets.length })}
                </label>
                <button
                  type="button"
                  className="sp-btn primary"
                  disabled={!pickedSheets.size || !!bulkSave}
                  onClick={savePickedSheets}
                >
                  {bulkSave
                    ? t('xOpa.savingNofM', { done: bulkSave.done, total: bulkSave.total })
                    : (pickedSheets.size ? t('xOpa.saveSelectedN', { n: pickedSheets.size }) : t('xOpa.saveSelected'))}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {!!leaving && (
        <div className="sp-modal-back">
          <div className="sp-modal sp-askbox" onClick={(e) => e.stopPropagation()}>
            <div className="sp-modal-h">
              <div>
                <strong>{t('xOpa.leaveTitle')}</strong>
                <small>{docNo || t('xOpa.newOpa')}</small>
              </div>
            </div>
            <div className="sp-modal-body">
              <p className="sp-note">
                {t('xOpa.leaveNote')}
              </p>
            </div>
            <div className="sp-modal-f">
              {/* Keep editing is the default and sits on the right, where the safe answer belongs. */}
              <button
                type="button"
                className="sp-btn sp-btn-danger"
                onClick={() => { const go = leaving; setLeaving(null); setSnapshot(''); go(); }}
              >
                {t('xOpa.discard')}
              </button>
              <button type="button" className="sp-btn primary" autoFocus onClick={() => setLeaving(null)}>
                {t('xOpa.keepEditing')}
              </button>
            </div>
          </div>
        </div>
      )}

      {!!skuView && (
        <div className="sp-modal-back">
          <div className="sp-modal opa-skubox" onClick={(e) => e.stopPropagation()}>
            <div className="sp-modal-h">
              <div>
                <strong>{t('xOpa.skuViewTitle')}</strong>
                <small>{skuView.doc.docNo} · {skuView.doc.styleName}</small>
              </div>
              <button type="button" className="sp-icon-btn" onClick={() => setSkuView(null)}>
                <X size={16} />
              </button>
            </div>
            <div className="sp-modal-body">
              <div className="opa-sku-scroll">
                <table className="sp-table opa-sku-table">
                  <thead>
                    <tr><th>{t('xOpa.hSku')}</th><th>{t('xOpa.hSap')}</th><th>{t('xOpa.hStyle')}</th><th>{t('xOpa.hColor')}</th></tr>
                  </thead>
                  <tbody>
                    {skuView.rows.map(({ sap, sku, name, color }) => (
                      <tr key={sap}>
                        <td className="opa-sku-code">{sku || '—'}</td>
                        <td className="opa-sku-code">{sap}</td>
                        <td>{name || '—'}</td>
                        <td>{color || '—'}</td>
                      </tr>
                    ))}
                    {!skuView.rows.length && (
                      <tr>
                        <td className="sp-empty" colSpan={4}>
                          {skuView.loading ? t('xOpa.loading') : t('xOpa.noSkus')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="sp-modal-f">
              <span className="opa-sku-count">{t('xOpa.nSkus', { n: skuView.rows.length })}</span>
              <button type="button" className="sp-btn primary" onClick={() => setSkuView(null)}>
                {t('xOpa.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Name the ticked rows and keep them. */}
      {/* Removing a block is not removing anybody's work: documents built from it keep their own
          copied rows. Worth saying, so nobody hesitates over the button. */}
      {!!dropBlock && (
        /* Opened FROM the library modal, so it has to sit above it. Every .sp-modal-back shares one
           z-index, and the library's footer was landing on top of this dialog's Remove button —
           clicking it did nothing at all (caught by the browser check, 2026-09-02). */
        <div className="sp-modal-back opa-over">
          <div className="sp-modal opa-dropbox" onClick={(e) => e.stopPropagation()}>
            <div className="sp-modal-h">
              <div><strong>{t('xOpa.dropBlockTitle')}</strong></div>
              <button type="button" className="sp-icon-btn" onClick={() => setDropBlock(null)}>
                <X size={16} />
              </button>
            </div>
            <div className="sp-modal-body">
              <p className="opa-dropname">{dropBlock.name}</p>
              <p className="opa-dropnote">
                {t('xOpa.dropBlockNote', { count: dropBlock.lineCount })}
              </p>
            </div>
            <div className="sp-modal-f">
              <button type="button" className="sp-btn" onClick={() => setDropBlock(null)}>{t('xOpa.cancel')}</button>
              <button type="button" className="sp-btn sp-btn-danger" onClick={removeBlock}>
                <Trash2 size={14} /> {t('xOpa.remove')}
              </button>
            </div>
          </div>
        </div>
      )}

      {!!saveBlock && (
        <div className="sp-modal-back">
          <div className="sp-modal opa-blockbox" onClick={(e) => e.stopPropagation()}>
            <div className="sp-modal-h">
              <div>
                <strong>{t('xOpa.saveAsBlock')}</strong>
                <small>{t('xOpa.opsFrom', { n: picked.size, sec: secLabel(section) })}</small>
              </div>
              <button type="button" className="sp-icon-btn" onClick={() => setSaveBlock(null)}>
                <X size={16} />
              </button>
            </div>
            <div className="sp-modal-body">
              {!!blockErr && <div className="sp-error">{blockErr}</div>}
              <label className="tfa-f">
                <span>{t('xOpa.name')}</span>
                <input
                  className="sp-input"
                  autoFocus
                  value={saveBlock.name}
                  placeholder="POLE POCKET — CHAIR ZERO"
                  onChange={(e) => setSaveBlock({ ...saveBlock, name: e.target.value })}
                />
              </label>
              <label className="tfa-f">
                <span>{t('xOpa.noteOpt')}</span>
                <input
                  className="sp-input"
                  value={saveBlock.note}
                  placeholder={t('xOpa.notePlaceholder')}
                  onChange={(e) => setSaveBlock({ ...saveBlock, note: e.target.value })}
                />
              </label>
              <ol className="opa-blockprev">
                {[...picked].sort((a, b) => a - b).map((i) => (
                  <li key={i}>{sectionLines[i]?.stageEn || sectionLines[i]?.stageVn || t('xOpa.blank')}</li>
                ))}
              </ol>
            </div>
            <div className="sp-modal-f">
              <button type="button" className="sp-btn" onClick={() => setSaveBlock(null)}>{t('xOpa.cancel')}</button>
              <button
                type="button"
                className="sp-btn primary"
                disabled={!saveBlock.name.trim()}
                onClick={storeBlock}
              >
                <Save size={14} /> {t('xOpa.saveBlock')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pick a block; its rows are copied into the section that is open. */}
      {insertOpen && (
        <div className="sp-modal-back">
          <div className="sp-modal opa-blockbox" onClick={(e) => e.stopPropagation()}>
            <div className="sp-modal-h">
              <div>
                <strong>{t('xOpa.insertBlock')}</strong>
                <small>{t('xOpa.copiedInto', { sec: secLabel(section) })}</small>
              </div>
              <button type="button" className="sp-icon-btn" onClick={() => setInsertOpen(false)}>
                <X size={16} />
              </button>
            </div>
            <div className="sp-modal-body">
              {!!blockErr && <div className="sp-error">{blockErr}</div>}
              <div className="opa-blocksearch">
                <input
                  className="sp-input"
                  value={blockSearch}
                  onChange={(e) => setBlockSearch(e.target.value)}
                  placeholder={t('xOpa.blockSearch')}
                />
                {!!blockSearch && (
                  <button type="button" className="sp-mini-btn" title={t('xOpa.clear')}
                    onClick={() => setBlockSearch('')}><X size={12} /></button>
                )}
                <small>{t('xOpa.nOfM', { n: shownBlocks.length, total: blocks.length })}</small>
              </div>
              <table className="sp-table">
                <thead>
                  <tr><th>{t('xOpa.hBlock')}</th><th className="num">{t('xOpa.hOps')}</th><th>{t('xOpa.hNote')}</th><th>{t('xOpa.hSavedBy')}</th><th /></tr>
                </thead>
                <tbody>
                  {/* A name and a count said nothing about what is in a block, so inserting one was
                      a guess you undid afterwards (Max, 2026-09-03). The rows ride along with the
                      list already — see opaBlockLines — so opening one costs no request. */}
                  {shownBlocks.map((b) => {
                    const open = blockOpen === b.id;
                    return (
                    <React.Fragment key={b.id}>
                    <tr className={open ? 'opa-block-on' : ''}>
                      <td>
                        <button
                          type="button"
                          className="opa-block-name"
                          title={open ? t('xOpa.hideOps') : t('xOpa.showOps')}
                          onClick={() => openBlock(b)}
                        >
                          <ChevronRight size={13} className={open ? 'rot' : ''} />
                          <strong>{b.name}</strong>
                        </button>
                      </td>
                      <td className="num">{b.lineCount}</td>
                      <td>{b.note || '—'}</td>
                      <td>{b.createdBy || '—'}</td>
                      <td className="opa-blockact">
                        {(() => {
                          const n = (open && blockPick) ? blockPick.size : b.lineCount;
                          return (
                            <button
                              type="button"
                              className="sp-mini-btn"
                              disabled={!n}
                              title={open ? t('xOpa.insertTicked') : t('xOpa.insertAll')}
                              onClick={() => insertBlock(b)}
                            >
                              <Plus size={12} /> {open ? t('xOpa.insertN', { n }) : t('xOpa.insert')}
                            </button>
                          );
                        })()}
                        {/* Add what is ticked in the document to a block that already exists —
                            Yen's 5. Reads the same selection the "Save as a standard block" button
                            does, so there is one idea of "the operations I mean". */}
                        {!readOnly && (
                          <button
                            type="button"
                            className="sp-mini-btn"
                            disabled={!picked.size}
                            title={picked.size
                              ? t('xOpa.addTickedTo', { n: picked.size, block: b.name })
                              : t('xOpa.tickInDocFirst')}
                            onClick={() => appendPickedToBlock(b)}
                          >
                            <BookmarkPlus size={12} /> {picked.size ? t('xOpa.addN', { n: picked.size }) : t('xOpa.add')}
                          </button>
                        )}
                        <button
                          type="button"
                          className="sp-mini-btn sp-mini-danger"
                          title={t('xOpa.removeFromLib')}
                          onClick={() => setDropBlock(b)}
                        >
                          <Trash2 size={12} />
                        </button>
                      </td>
                    </tr>
                    {open && (
                      <tr className="opa-block-rows">
                        <td colSpan={5}>
                          {/* Its own scroller: without one the nested table stretched the outer table
                              to 1,534px inside a 1,320px dialog, and the overflow was clipped rather
                              than reachable (Max, 2026-09-03). */}
                          <div className="opa-blockscroll">
                          <table className="sp-table opa-blocklines">
                            <thead>
                              <tr>
                                <th className="opa-pick">
                                  <input
                                    type="checkbox"
                                    title={t('xOpa.allOpsInBlock')}
                                    checked={!!blockPick && blockPick.size === (b.lines || []).length && !!(b.lines || []).length}
                                    onChange={(e) => setBlockPick(e.target.checked
                                      ? new Set((b.lines || []).map((_, i) => i)) : new Set())}
                                  />
                                </th>
                                <th>{t('xOpa.hNo')}</th><th>{t('xOpa.hStages')}</th><th>{t('xOpa.hStagesVn')}</th>
                                <th>{t('xOpa.hNeedleShort')}</th><th>{t('xOpa.hMachineShort')}</th>
                                <th className="num">{t('xOpa.hLengthShort')}</th><th className="num">{t('xOpa.hQty')}</th>
                                <th className="num">{t('xOpa.hCtPartShort')}</th><th className="num">{t('xOpa.hCt')}</th>
                                <th className="mid">{t('xOpa.hPhoto')}</th>
                                {!readOnly && <th />}
                              </tr>
                            </thead>
                            <tbody>
                              {(b.lines || []).map((l, i) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <tr key={i} className={blockPick && !blockPick.has(i) ? 'off' : ''}>
                                  <td className="opa-pick">
                                    <input
                                      type="checkbox"
                                      checked={!!blockPick && blockPick.has(i)}
                                      onChange={() => togglePickLine(i)}
                                      aria-label={t('xOpa.takeOp', { n: i + 1 })}
                                    />
                                  </td>
                                  <td className="sp-rownum">{i + 1}</td>
                                  <td>{l.stageEn || '—'}</td>
                                  <td>{l.stageVn || '—'}</td>
                                  <td>{l.needle || '—'}</td>
                                  <td>{l.machine || '—'}</td>
                                  <td className="num">{l.sewingLengthCm === '' ? '—' : l.sewingLengthCm}</td>
                                  <td className="num">{l.partQty === '' ? '—' : l.partQty}</td>
                                  <td className="num">{l.ctPerPartSec === '' ? '—' : l.ctPerPartSec}</td>
                                  <td className="num opa-calc">{int(opaLineSeconds(l))}</td>
                                  <td className="mid">
                                    {l.photoId ? (
                                      <button
                                        type="button"
                                        className="opa-thumb"
                                        title={t('xOpa.viewPhoto')}
                                        onClick={() => setLightbox(l.photoId)}
                                      >
                                        <img src={PHOTO_URL(l.photoId)} alt={l.stageEn || t('xOpa.opN', { n: i + 1 })} />
                                      </button>
                                    ) : '—'}
                                  </td>
                                  {/* Takes the operation OUT OF THE BLOCK — not out of a document
                                      built from it, which keeps its own copied rows. Yen's 5. */}
                                  {!readOnly && (
                                    <td className="mid">
                                      <button
                                        type="button"
                                        className="sp-mini-btn sp-mini-danger"
                                        title={t('xOpa.removeOpFrom', { block: b.name })}
                                        onClick={() => dropBlockLine(b, i)}
                                      >
                                        <Trash2 size={12} />
                                      </button>
                                    </td>
                                  )}
                                </tr>
                              ))}
                              {!(b.lines || []).length && (
                                <tr><td className="sp-empty" colSpan={readOnly ? 11 : 12}>{t('xOpa.noOpsInBlock')}</td></tr>
                              )}
                            </tbody>
                          </table>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                    );
                  })}
                  {!shownBlocks.length && (
                    <tr><td className="sp-empty" colSpan={5}>
                      {blocks.length ? t('xOpa.noBlockMatch', { q: blockSearch }) : t('xOpa.nothingSaved')}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="sp-modal-f">
              <button type="button" className="sp-btn" onClick={() => setInsertOpen(false)}>{t('xOpa.close')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Full size. Its own overlay class rather than .st-modal-overlay: every one of those shares a
          z-index, and this can be opened from a screen that already has a dialog on it. */}
      {!!lightbox && (
        <div
          className="opa-lightbox"
          role="dialog"
          aria-modal="true"
          onClick={() => setLightbox(null)}
        >
          <button type="button" className="opa-lightbox-x" title={t('xOpa.close')} onClick={() => setLightbox(null)}>
            <X size={18} />
          </button>
          <img src={PHOTO_URL(lightbox)} alt={t('xOpa.altOperation')} onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {!!confirmDel && (
        <div className="sp-modal-back" onClick={() => setConfirmDel(null)}>
          <div className="sp-modal sp-askbox" onClick={(e) => e.stopPropagation()}>
            <div className="sp-modal-h">
              <div>
                <strong>{t('xOpa.delTitle')}</strong>
                <small>{confirmDel.docNo} · {confirmDel.styleName}</small>
              </div>
            </div>
            <div className="sp-modal-body">
              <p className="sp-note">
                {t('xOpa.delNote')}
              </p>
            </div>
            <div className="sp-modal-f">
              <button type="button" className="sp-btn" onClick={() => setConfirmDel(null)}>{t('xOpa.cancel')}</button>
              <button type="button" className="sp-btn sp-btn-danger" onClick={() => remove(confirmDel.id)}>
                <Trash2 size={13} /> {t('xOpa.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
